import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal for homepage sections.
 *
 * Returns a ref to put on a container. Every descendant carrying `revealClass`
 * gets `revealedClass` added once it scrolls into view, then stops being
 * observed.
 *
 * Both class names must be the *hashed* values from a CSS Module
 * (`shared.rv`, `shared.revealed`). Neither argument defaults, on purpose:
 * passing a literal like 'in' compiles to a different name than the module's
 * `.in`, so the class lands on the element, matches no rule, and every section
 * stays invisible with nothing in the console to say why.
 *
 * The hidden starting state lives behind an `.js` class on <html> (set by an
 * inline script in public/index.html), so anything that never runs this hook —
 * prerendered HTML, crawlers, JS turned off — shows the content outright.
 * That inversion is the whole point: the previous arrangement would have
 * shipped opacity:0 to exactly the readers prerendering exists to serve.
 *
 * No IntersectionObserver (old browsers, jsdom) means everything is revealed
 * immediately rather than left hidden.
 */
export default function useReveal(revealClass, revealedClass) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !revealClass || !revealedClass) return undefined;

    const targets = Array.from(root.querySelectorAll(`.${revealClass}`));
    if (targets.length === 0) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add(revealedClass));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(revealedClass);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );

    // Stagger within a row of cards, matching the mockup.
    targets.forEach((el, i) => {
      el.style.transitionDelay = `${(i % 4) * 60}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [revealClass, revealedClass]);

  return containerRef;
}
